/* The month at a glance — STORY-014.
 *
 * ── the grid says WHERE and the agenda says WHAT ────────────────────────────
 * The component's own note records why: chips truncate to two or three words in
 * a 1100px window with a sidebar, so the list beneath the grid turned out to be
 * the primary reading surface at every width rather than the phone's special
 * case. One list doing both jobs.
 *
 * ── month names and the first weekday are locale DATA ───────────────────────
 * No catalogue reaches them and no constant may hold them: it is Monday in much
 * of the world, Sunday in much of the rest, Saturday in some. They come from
 * `Intl` and from `firstWeekdayOf`, recomputed on a language change — the
 * prototype computed them once at load and they stayed English.
 *
 * ── nothing is silently omitted ─────────────────────────────────────────────
 * Undated Events and unfinished undated Tasks have their own area beneath the
 * month. An idea-stage Event would otherwise vanish from the only screen that
 * shows everything, and an undated Task wins the same argument.
 *
 * ── priority is never colour alone ──────────────────────────────────────────
 * The workbook used coloured dots with no text. Every chip carries its kind in
 * words, and a Task carries its priority as a word beside it.
 */

import type { CalendarEvent, CalendarTask } from '@eventuley/contracts';
import { firstWeekdayOf, monthGrid, monthOf, shiftMonth, todayIn } from '@eventuley/domain';
import type { MessageKey } from '@eventuley/i18n';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useState } from 'react';
import { z } from 'zod';
import { NetworkError } from '../../../shared/api/client.js';
import { useI18n, useTranslate } from '../../../shared/i18n/index.js';
import { Button, Callout } from '../../../shared/ui/index.js';
import { useCalendarMonth } from '../api/index.js';

/* Only the two that change how a row should be read. A planned Event and a
 * confirmed one are both "coming up" here, and labelling every row would be
 * noise on the screen whose job is scanning. */
const EVENT_STATUS_LABELS = {
  completed: 'events.status.completed',
  cancelled: 'events.status.cancelled',
} as const satisfies Record<'completed' | 'cancelled', MessageKey>;

const PRIORITY_LABELS = {
  low: 'tasks.priority.low',
  medium: 'tasks.priority.medium',
  high: 'tasks.priority.high',
} as const satisfies Record<CalendarTask['priority'], MessageKey>;

/**
 * The month, in the URL — so a selection survives a reload and a link can be
 * shared.
 *
 * `.catch()`ed rather than validated strictly: a hand-edited or stale link
 * degrades to this month rather than throwing, because a month nobody can read
 * is not worth a broken screen.
 *
 * Exported so the app's route tree and the test tree use ONE schema. A copy in
 * the test tree would let these tests prove something the product does not do
 * — which is exactly what happened before this was shared.
 */
export const calendarSearchSchema = z.object({
  month: z
    .string()
    .regex(/^\d{4}-(0[1-9]|1[0-2])$/)
    .optional()
    .catch(undefined),
});

export function CalendarScreen() {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();
  const { organisationId } = useParams({ strict: false }) as { organisationId: string };
  const navigate = useNavigate();
  const search = useSearch({ strict: false }) as { month?: string };

  /* The month lives in the URL, so the selection survives a reload and a link
   * can be shared — the story asks for persistence and this is the cheapest
   * honest way to get it.
   *
   * Today's month when nothing is asked for, in the READER's zone. `toDateString`
   * reads UTC parts, so using it here opened the calendar on AUGUST for an
   * Organiser in Istanbul at 01:00 on 1 September — and put the "today" ring on
   * the 31st. The comment claimed the reader's zone while the code used UTC,
   * which is the worst combination: it reads as considered. */
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = todayIn(timeZone, new Date());
  const thisMonth = monthOf(today);
  const month = search.month ?? thisMonth;

  const [selected, setSelected] = useState<string | null>(null);

  const { data: answer, error, isLoading } = useCalendarMonth(organisationId, month);

  /* The previous month's payload is kept while the next loads so the grid does
   * not blank — but it must not be READ as this month's. Without the guard,
   * clicking "next" redrew October's cells with September's Events counted into
   * them, and the agenda listed September under an October heading. The
   * response carries the month it is for, which is the whole fix. */
  const data = answer?.month === month ? answer : undefined;

  const goTo = (next: string) => {
    setSelected(null);
    void navigate({ to: '.', search: { month: next } });
  };

  /* Locale data, recomputed on every render rather than memoised at module
   * load — a language change must move the week's first day and the month's
   * name with it. */
  const firstWeekday = firstWeekdayOf(locale);
  const days = monthGrid(month, firstWeekday);

  const monthName = new Intl.DateTimeFormat(locale, {
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${month}-01T00:00:00Z`));

  /* Seven headers, generated from the locale rather than from a list. Starting
   * at a known Sunday and stepping, so the order follows `firstWeekday`. */
  const KNOWN_SUNDAY = Date.UTC(2026, 0, 4);
  const weekdayNames = Array.from({ length: 7 }, (_, index) => {
    const day = new Date(KNOWN_SUNDAY + ((firstWeekday + index) % 7) * 86_400_000);
    return {
      short: new Intl.DateTimeFormat(locale, { weekday: 'short', timeZone: 'UTC' }).format(day),
      long: new Intl.DateTimeFormat(locale, { weekday: 'long', timeZone: 'UTC' }).format(day),
    };
  });

  const eventsOn = (date: string) => (data?.events ?? []).filter((event) => event.date === date);
  const tasksOn = (date: string) => (data?.tasks ?? []).filter((task) => task.dueOn === date);

  const weeks = Array.from({ length: days.length / 7 }, (_, index) =>
    days.slice(index * 7, index * 7 + 7),
  );

  const nothingAtAll =
    !isLoading &&
    (data?.events.length ?? 0) === 0 &&
    (data?.tasks.length ?? 0) === 0 &&
    (data?.undatedEvents.length ?? 0) === 0 &&
    (data?.undatedTasks.length ?? 0) === 0;

  return (
    <>
      <div className="page-head">
        <h1>{t('calendar.title')}</h1>
        <div className="btn-row">
          <Button variant="ghost" onClick={() => goTo(shiftMonth(month, -1))}>
            {t('calendar.previous')}
          </Button>
          <Button variant="ghost" onClick={() => goTo(thisMonth)}>
            {t('calendar.today')}
          </Button>
          <Button variant="ghost" onClick={() => goTo(shiftMonth(month, 1))}>
            {t('calendar.next')}
          </Button>
        </div>
      </div>

      {error ? null : (
        <div className="table-wrap">
          <table className="cal">
            {/* The month's name is the table's caption rather than a heading
              beside it: a screen reader announcing the table announces which
              month it is showing, which is the one thing a grid of numbers
              cannot say for itself. */}
            <caption className="cal__caption">{monthName}</caption>
            <thead>
              <tr>
                {weekdayNames.map((weekday) => (
                  <th scope="col" key={weekday.long}>
                    <abbr title={weekday.long}>{weekday.short}</abbr>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {weeks.map((week) => (
                <tr key={week[0]?.date}>
                  {week.map((day) => {
                    const dayEvents = eventsOn(day.date);
                    const dayTasks = tasksOn(day.date);
                    const count = dayEvents.length + dayTasks.length;
                    const isToday = day.date === today;

                    return (
                      <td
                        key={day.date}
                        className={[
                          'cal__cell',
                          day.inMonth ? '' : 'cal__cell--adjacent',
                          isToday ? 'cal__cell--today' : '',
                          selected === day.date ? 'cal__cell--selected' : '',
                        ]
                          .filter(Boolean)
                          .join(' ')}
                      >
                        <button
                          type="button"
                          className="cal__day"
                          /* Selection is announced as well as shown — the filled
                           day number is not enough on its own. */
                          aria-pressed={selected === day.date}
                          /* The date and what is on it, in words. A grid of bare
                           numbers tells a screen reader nothing about which is
                           worth opening. */
                          aria-label={t('calendar.day_label', {
                            date: new Intl.DateTimeFormat(locale, {
                              dateStyle: 'full',
                              timeZone: 'UTC',
                            }).format(new Date(`${day.date}T00:00:00Z`)),
                            count,
                          })}
                          onClick={() => setSelected(selected === day.date ? null : day.date)}
                        >
                          <span className="cal__daynum">{Number(day.date.slice(-2))}</span>
                          {count > 0 ? <span className="cal__count">{count}</span> : null}
                        </button>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* The error sits INSIDE the screen rather than replacing it, so the
          month controls above stay reachable. Returning early left a reader
          whose October request failed with no way back to September short of
          editing the URL. */}
      {error ? (
        <Callout tone="danger">
          {error instanceof NetworkError ? t('error.network') : t('error.unexpected')}
        </Callout>
      ) : null}

      {/* A grid of zeroes is indistinguishable from a genuinely quiet month on
          a slow connection. */}
      {isLoading && data === undefined ? (
        <p className="field__hint">{t('common.loading')}</p>
      ) : null}

      {nothingAtAll ? (
        /* Not a blank grid and not an error — most months of most workspaces
           look like this, and it must not read as something having failed. */
        <Callout tone="info">{t('calendar.nothing_this_month')}</Callout>
      ) : null}

      <Agenda
        organisationId={organisationId}
        events={data?.events ?? []}
        tasks={data?.tasks ?? []}
        selected={selected}
        locale={locale}
        onClearSelection={() => setSelected(null)}
      />

      {/* Never silently omitted. An idea would otherwise vanish from the only
          screen that shows everything, and an unfinished undated Task wins the
          same argument — decided 2026-08-14. */}
      {(data?.undatedEvents.length ?? 0) > 0 || (data?.undatedTasks.length ?? 0) > 0 ? (
        <section className="settings__section" aria-labelledby="undated-heading">
          <h2 id="undated-heading">{t('calendar.undated_heading')}</h2>
          <p className="field__hint">{t('calendar.undated_hint')}</p>
          <ul className="row-list">
            {(data?.undatedEvents ?? []).map((event) => (
              <EventRow key={event.id} organisationId={organisationId} event={event} />
            ))}
            {(data?.undatedTasks ?? []).map((task) => (
              <TaskRow key={task.id} task={task} />
            ))}
          </ul>
        </section>
      ) : null}
    </>
  );
}

/**
 * What is on the days, as a list.
 *
 * The primary reading surface at every width, not the phone's fallback — chips
 * in a cell truncate to two or three words once a sidebar is eating the window.
 * Filtered to one day when a day is selected, and the whole month otherwise.
 */
function Agenda({
  organisationId,
  events,
  tasks,
  selected,
  locale,
  onClearSelection,
}: {
  readonly organisationId: string;
  readonly events: readonly CalendarEvent[];
  readonly tasks: readonly CalendarTask[];
  readonly selected: string | null;
  readonly locale: string;
  readonly onClearSelection: () => void;
}) {
  const t = useTranslate();

  const shown = selected === null;
  const visibleEvents = shown ? events : events.filter((event) => event.date === selected);
  const visibleTasks = shown ? tasks : tasks.filter((task) => task.dueOn === selected);

  if (visibleEvents.length === 0 && visibleTasks.length === 0) {
    return selected === null ? null : (
      <Callout tone="info">
        <p>{t('calendar.nothing_that_day')}</p>
        <div className="btn-row">
          <Button variant="ghost" onClick={onClearSelection}>
            {t('calendar.show_whole_month')}
          </Button>
        </div>
      </Callout>
    );
  }

  /* By date, and events before tasks on the same day: an Event is a fixed
   * point somebody has to be somewhere for, a Task is work that can move. */
  const byDate = <T extends { readonly key: string }>(rows: readonly T[]) =>
    [...rows].sort((a, b) => a.key.localeCompare(b.key));

  const rows = byDate([
    ...visibleEvents.map((event) => ({
      key: `${event.date ?? ''}-0-${event.id}`,
      node: <EventRow key={event.id} organisationId={organisationId} event={event} />,
    })),
    ...visibleTasks.map((task) => ({
      key: `${task.dueOn ?? ''}-1-${task.id}`,
      node: <TaskRow key={task.id} task={task} />,
    })),
  ]);

  return (
    <section className="settings__section" aria-labelledby="agenda-heading">
      <h2 id="agenda-heading">
        {selected === null
          ? t('calendar.agenda_month')
          : t('calendar.agenda_day', {
              date: new Intl.DateTimeFormat(locale, {
                dateStyle: 'long',
                timeZone: 'UTC',
              }).format(new Date(`${selected}T00:00:00Z`)),
            })}
      </h2>
      {selected === null ? null : (
        <div className="btn-row">
          <Button variant="ghost" onClick={onClearSelection}>
            {t('calendar.show_whole_month')}
          </Button>
        </div>
      )}
      <ul className="row-list">{rows.map((row) => row.node)}</ul>
    </section>
  );
}

function EventRow({
  organisationId,
  event,
}: {
  readonly organisationId: string;
  readonly event: CalendarEvent;
}) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">
          <Link
            to="/orgs/$organisationId/events/$eventId"
            params={{ organisationId, eventId: event.id }}
          >
            {event.name}
          </Link>
        </span>
        {/* The kind in words, never a coloured dot. The workbook used dots and
            they said nothing to anybody who could not see the colour. */}
        <span className="row__meta">
          {event.date
            ? t('calendar.event_on', {
                date: new Intl.DateTimeFormat(locale, {
                  dateStyle: 'medium',
                  timeZone: 'UTC',
                }).format(new Date(`${event.date}T00:00:00Z`)),
              })
            : t('calendar.event_undated')}
        </span>
        {event.venue ? <span className="row__meta">{event.venue.name}</span> : null}
        {/* A cancelled or completed Event stays on its day — it happened, or it
            was going to — but it must not look identical to one still on. The
            events list hides both by default, so this screen is the only place
            they appear and the only place the distinction can be lost. In
            words, never a colour. */}
        {event.status === 'cancelled' || event.status === 'completed' ? (
          <span className="badge">{t(EVENT_STATUS_LABELS[event.status])}</span>
        ) : null}
      </span>
    </li>
  );
}

function TaskRow({ task }: { readonly task: CalendarTask }) {
  const t = useTranslate();
  const { formatLocale: locale } = useI18n();

  return (
    <li className="row">
      <span className="row__main">
        <span className="row__title">{task.description}</span>
        <span className="row__meta">
          {task.dueOn
            ? t('calendar.task_due', {
                date: new Intl.DateTimeFormat(locale, {
                  dateStyle: 'medium',
                  timeZone: 'UTC',
                }).format(new Date(`${task.dueOn}T00:00:00Z`)),
                priority: t(PRIORITY_LABELS[task.priority]),
              })
            : t('calendar.task_undated', { priority: t(PRIORITY_LABELS[task.priority]) })}
        </span>
      </span>
    </li>
  );
}
